import { LinkOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Input, Space, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { PayrollEmployeeCheckDto, ShiftListItemDto } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';
import { toDollarAmount } from '@clh/util';

import { useSelection } from '../../hooks/use-selection';
import { useToast } from '../../hooks/use-toast';
import { getZealCheckUrl } from '../../payroll-invoicing/zeal-url';
import ObjectPropertyList from '../../record-details/object-property-list';

const { Text } = Typography;

export default function PayTab() {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();
    const { notification } = useToast();

    const [editingPayPeriodId, setEditingPayPeriodId] = useState<string | null>(
        null
    );
    const [zealCheckId, setZealCheckId] = useState('');
    const [loading, setLoading] = useState(false);
    const [checkDataMap, setCheckDataMap] = useState<
        Map<string, PayrollEmployeeCheckDto>
    >(new Map());

    useEffect(() => {
        const fetchChecks = async () => {
            if (!api || !selection.id) return;

            try {
                const checks = await api.payrollControllerGetPaychecks({
                    search: selection.id,
                });

                const newCheckMap = new Map<string, PayrollEmployeeCheckDto>();

                checks?.forEach((check) => {
                    check.payPeriods?.forEach((payPeriod) => {
                        const matchingShiftPayPeriod =
                            selection.payPeriods?.find(
                                (spp) => spp.id === payPeriod.id
                            );
                        if (matchingShiftPayPeriod) {
                            newCheckMap.set(payPeriod.id, check);
                        }
                    });
                });

                setCheckDataMap(newCheckMap);
            } catch (err) {
                notification.error({ message: unwrapError(err) });
            }
        };

        void fetchChecks();
    }, [api, selection.id, selection.payPeriods]);

    const handleSetZealCheck = async (payPeriodId: string) => {
        setLoading(true);
        try {
            const result = await api?.payrollControllerSetPayPeriodZealCheck({
                payPeriodId,
                setZealCheckDto: { zealEmployeeCheckId: zealCheckId },
            });

            if (!result) {
                throw new Error('No response from API');
            }

            const updatedPayPeriods = selection.payPeriods?.map((pp) =>
                pp.id === payPeriodId
                    ? { ...pp, zealEmployeeCheckId: result.zealEmployeeCheckId }
                    : pp
            );

            setCheckDataMap((prev) => new Map(prev).set(payPeriodId, result));

            setSelection({ ...selection, payPeriods: updatedPayPeriods });

            notification.success({
                message: 'Zeal check ID updated successfully',
                duration: 3,
            });
            setEditingPayPeriodId(null);
            setZealCheckId('');
        } catch (err) {
            notification.error({
                message: unwrapError(err),
                duration: 3,
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date: Date) => {
        return DateTime.fromJSDate(new Date(date)).toFormat('MMM dd, yyyy');
    };

    const formatCurrency = (cents: number | null | undefined) => {
        if (cents == null) return '-';
        return toDollarAmount(cents);
    };

    if (!selection.payPeriods || selection.payPeriods.length === 0) {
        return (
            <Typography.Text type="secondary">
                No pay periods found for this shift
            </Typography.Text>
        );
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Typography.Title level={4}>Pay Periods</Typography.Title>
            {selection.payPeriods.map((payPeriod) => {
                const isEditing = editingPayPeriodId === payPeriod.id;
                const checkData = checkDataMap.get(payPeriod.id);

                return (
                    <Card
                        key={payPeriod.id}
                        title={`${formatDate(
                            payPeriod.startDate
                        )} - ${formatDate(payPeriod.endDate)}`}
                        style={{ marginBottom: 16 }}
                    >
                        <Flex
                            justify="space-between"
                            align="center"
                            style={{ marginBottom: 16 }}
                        >
                            {isEditing ? (
                                <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Text type="secondary">
                                        Enter the ID of the check from Zeal's
                                        payment portal. This can be found at the
                                        top of the employee check page labeled
                                        "Employee Check ID".
                                    </Text>
                                    <Space>
                                        <Input
                                            value={zealCheckId}
                                            onChange={(e) =>
                                                setZealCheckId(e.target.value)
                                            }
                                            placeholder="Enter Zeal Check ID"
                                            style={{ width: 250 }}
                                        />
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                void handleSetZealCheck(
                                                    payPeriod.id
                                                );
                                            }}
                                            loading={loading}
                                        >
                                            Submit
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setEditingPayPeriodId(null);
                                                setZealCheckId('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Space>
                                </Space>
                            ) : (
                                <Button
                                    onClick={() => {
                                        setEditingPayPeriodId(payPeriod.id);
                                        setZealCheckId(
                                            payPeriod.zealEmployeeCheckId || ''
                                        );
                                    }}
                                >
                                    {payPeriod.zealEmployeeCheckId
                                        ? 'Change'
                                        : 'Set'}{' '}
                                    Zeal Check ID
                                </Button>
                            )}
                            {checkData && (
                                <Button
                                    type="link"
                                    icon={<LinkOutlined />}
                                    href={getZealCheckUrl(checkData)}
                                    target="_blank"
                                >
                                    Open in Zeal
                                </Button>
                            )}
                        </Flex>

                        <ObjectPropertyList
                            values={{
                                minutesWorked: payPeriod.minutesWorked,
                                onCallMinutesWorked:
                                    payPeriod.onCallMinutesWorked,
                                zealEmployeeCheckId:
                                    payPeriod.zealEmployeeCheckId,
                                ...(checkData && {
                                    grossPay: checkData.grossPay,
                                    netPay: checkData.netPay,
                                    overtimePay: checkData.overtimePay,
                                    checkDate: checkData.checkDate,
                                    status: checkData.status,
                                    isApproved: checkData.isApproved,
                                }),
                            }}
                            fields={{
                                minutesWorked: true,
                                onCallMinutesWorked: true,
                                zealEmployeeCheckId: (
                                    checkId: string | null
                                ) =>
                                    checkId ? (
                                        <Text code copyable>
                                            {checkId}
                                        </Text>
                                    ) : (
                                        <Text type="secondary">Not set</Text>
                                    ),
                                ...(checkData && {
                                    grossPay: (cents: number) => (
                                        <>{formatCurrency(cents)}</>
                                    ),
                                    netPay: (cents: number) => (
                                        <>{formatCurrency(cents)}</>
                                    ),
                                    overtimePay: (cents: number | null) => (
                                        <>{formatCurrency(cents)}</>
                                    ),
                                    checkDate: (date: Date) => (
                                        <>{formatDate(date)}</>
                                    ),
                                    status: true,
                                    isApproved: (approved: boolean) => (
                                        <>{approved ? 'Yes' : 'No'}</>
                                    ),
                                }),
                            }}
                        />
                    </Card>
                );
            })}
        </Space>
    );
}
